import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Download, Upload, AlertTriangle, Globe } from 'lucide-react';
import { validateRut, formatRut } from '@/utils/chile';

const emptyCompany = { name: '', razon_social: '', rut: '', giro: '', industry: '', website: '', phone: '', email: '', address: '', region: '', comuna: '' };

export default function CompaniesPage() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState(emptyCompany);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [regiones, setRegiones] = useState<any[]>([]);
  const [giros, setGiros] = useState<string[]>([]);
  const [rutValid, setRutValid] = useState<boolean | null>(null);
  const { currentOrgId } = useOrgStore();

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return;
    const params: any = {};
    if (search) params.search = search;
    const { data } = await api.get('/companies', { params });
    setCompanies(data);
  }, [currentOrgId, search]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    api.get('/chile/regiones').then(r => setRegiones(r.data)).catch(() => {});
    api.get('/chile/giros').then(r => setGiros(r.data)).catch(() => {});
  }, []);

  const comunas = regiones.find(r => r.name === form.region)?.comunas || [];

  const handleRutChange = (value: string) => {
    const formatted = formatRut(value);
    setForm({ ...form, rut: formatted });
    if (formatted.length >= 3) {
      const { valid } = validateRut(formatted);
      setRutValid(valid);
    } else {
      setRutValid(null);
    }
  };

  const openCreate = () => { setEditItem(null); setForm(emptyCompany); setRutValid(null); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setEditItem(item);
    setForm({ name: item.name || '', razon_social: item.razon_social || '', rut: item.rut || '', giro: item.giro || '', industry: item.industry || '', website: item.website || '', phone: item.phone || '', email: item.email || '', address: item.address || '', region: item.region || '', comuna: item.comuna || '' });
    if (item.rut && item.rut.length >= 3) { setRutValid(validateRut(item.rut).valid); } else { setRutValid(null); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) { await api.put(`/companies/${editItem.id}`, form); toast.success('Empresa actualizada'); }
      else { await api.post('/companies', form); toast.success('Empresa creada'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/companies/${deleteId}`); toast.success('Empresa eliminada'); setDeleteId(null); fetchData(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/companies/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'empresas.csv'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('CSV exportado');
    } catch { toast.error('Error al exportar'); }
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const formData = new FormData(); formData.append('file', file);
    try { const { data } = await api.post('/companies/import/csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${data.count} empresas importadas`); fetchData(); }
    catch { toast.error('Error al importar'); }
    setImporting(false); e.target.value = '';
  };

  return (
    <div data-testid="companies-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Empresas</h1>
          <p className="text-sm text-[#475569] mt-1">{companies.length} empresas registradas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button variant="outline" onClick={handleExportCSV} className="flex-1 sm:flex-none min-h-11 border-[#E2E8F0] text-[#475569] font-medium" data-testid="export-companies-csv"><Download size={16} className="mr-2" /> Exportar</Button>
          <div className="relative flex-1 sm:flex-none">
            <input type="file" accept=".csv" onChange={handleImportCSV} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <Button variant="outline" className="w-full sm:w-auto min-h-11 border-[#E2E8F0] text-[#475569] font-medium pointer-events-none" disabled={importing}><Upload size={16} className="mr-2" /> {importing ? 'Importando...' : 'Importar'}</Button>
          </div>
          <Button onClick={openCreate} className="flex-1 sm:flex-none min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold" data-testid="create-company-btn">
            <Plus size={16} className="mr-2" /> Nueva Empresa
          </Button>
        </div>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input placeholder="Buscar empresas..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E2E8F0]" data-testid="companies-search" />
      </div>
      <div className="hidden lg:block bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="companies-table">
            <thead><tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Nombre Fantasia</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden md:table-cell">Razon Social</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">RUT</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Giro</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden lg:table-cell">Sitio Web</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">Region / Comuna</th>
              <th className="py-3 px-4 w-12"></th>
            </tr></thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors">
                  <td className="py-3 px-4 font-medium text-[#0F172A]">
                    <Link to={`/empresas/${c.id}`} className="text-[#2563EB] hover:underline" data-testid={`company-link-${c.id}`}>
                      {c.name}
                    </Link>
                  </td>
                  <td className="py-3 px-4 text-[#475569] hidden md:table-cell text-xs">{c.razon_social || '-'}</td>
                  <td className="py-3 px-4 font-mono text-xs text-[#475569]">{c.rut || '-'}</td>
                  <td className="py-3 px-4 text-[#475569] hidden lg:table-cell text-xs">{c.giro || c.industry || '-'}</td>
                  <td className="py-3 px-4 hidden lg:table-cell">
                    {c.website ? <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] hover:underline text-xs"><Globe size={12} />{c.website}</a> : '-'}
                  </td>
                  <td className="py-3 px-4 text-[#475569] text-xs hidden xl:table-cell">{c.region ? `${c.region}${c.comuna ? ` / ${c.comuna}` : ''}` : '-'}</td>
                  <td className="py-3 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteId(c.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {companies.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-[#94A3B8]">No hay empresas</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <ul className="lg:hidden space-y-3" data-testid="companies-card-list">
        {companies.map((c) => (
          <li key={c.id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/empresas/${c.id}`} className="font-semibold text-[#2563EB] hover:underline" data-testid={`company-link-${c.id}`}>
                  {c.name}
                </Link>
                <p className="text-xs text-[#475569] mt-0.5 truncate">{c.razon_social || 'Sin razón social'}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-11 w-11 shrink-0"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEdit(c)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDeleteId(c.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-y-1.5 gap-x-3 text-sm">
              <dt className="text-[#64748B]">RUT</dt>
              <dd className="text-right font-mono text-xs text-[#475569] tabular-nums">{c.rut || '-'}</dd>
              <dt className="text-[#64748B]">Giro</dt>
              <dd className="text-right text-xs text-[#475569]">{c.giro || c.industry || '-'}</dd>
              <dt className="text-[#64748B]">Sitio Web</dt>
              <dd className="text-right truncate">
                {c.website ? <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] hover:underline text-xs"><Globe size={12} />{c.website}</a> : <span className="text-[#475569] text-xs">-</span>}
              </dd>
              <dt className="text-[#64748B]">Región / Comuna</dt>
              <dd className="text-right text-xs text-[#475569]">{c.region ? `${c.region}${c.comuna ? ` / ${c.comuna}` : ''}` : '-'}</dd>
            </dl>
          </li>
        ))}
        {companies.length === 0 && (
          <li className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-8 text-center text-sm text-[#94A3B8]">
            No hay empresas
          </li>
        )}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="company-dialog">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editItem ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre de Fantasia</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E2E8F0]" data-testid="company-name-input" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Razon Social</Label>
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} placeholder="Nombre legal para facturacion" className="border-[#E2E8F0]" data-testid="company-razon-social" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">RUT</Label>
                <div className="relative">
                  <Input value={form.rut} onChange={(e) => handleRutChange(e.target.value)} placeholder="XX.XXX.XXX-X" className={`border-[#E2E8F0] font-mono ${rutValid === false ? 'border-[#EF4444] focus:ring-[#EF4444]/20' : rutValid === true ? 'border-[#22C55E] focus:ring-[#22C55E]/20' : ''}`} data-testid="company-rut-input" />
                  {rutValid === false && <div className="flex items-center gap-1 mt-1"><AlertTriangle size={12} className="text-[#EF4444]" /><span className="text-[10px] text-[#EF4444] font-medium">RUT invalido (digito verificador incorrecto)</span></div>}
                  {rutValid === true && <span className="text-[10px] text-[#22C55E] font-medium mt-1 block">RUT valido</span>}
                </div>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Giro (SII)</Label>
                <Select value={form.giro || "none"} onValueChange={(v) => setForm({ ...form, giro: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="company-giro-select"><SelectValue placeholder="Seleccionar giro" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {giros.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Sitio Web</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.empresa.cl" className="border-[#E2E8F0]" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-[#E2E8F0]" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+56 2 XXXX XXXX" className="border-[#E2E8F0]" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Direccion</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="border-[#E2E8F0]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Region</Label>
                <Select value={form.region || "none"} onValueChange={(v) => setForm({ ...form, region: v === "none" ? "" : v, comuna: '' })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="company-region-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {regiones.map(r => <SelectItem key={r.code} value={r.name}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Comuna</Label>
                <Select value={form.comuna || "none"} onValueChange={(v) => setForm({ ...form, comuna: v === "none" ? "" : v })} disabled={!form.region}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="company-comuna-select"><SelectValue placeholder={form.region ? 'Seleccionar' : 'Elija region'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {comunas.map((c: string) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="company-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar Empresa</AlertDialogTitle>
          <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
